import { BOX_CONDITIONS, SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { getFirstLowestPriceVariants } from 'shared/utils/getFirstLowestPriceVariants';
import { IBuyBarProductVariant, IMapSizeToVariants } from 'shared/types/BuyBarProductVariant';
import { ISize } from 'shared/types/Size';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';

const createSizeToVariantObject = (
  mapObject: IMapSizeToVariants,
  variants: IBuyBarProductVariant[],
  key: VARIANT_KEYS,
) => {
  for (const variant of variants) {
    if (!variant?.sizeOption?.value && variant?.sizeOption?.value !== 0) return;

    if (variant.sizeOption.value in mapObject) {
      mapObject[variant.sizeOption.value][key] = variant;
    } else {
      mapObject[variant.sizeOption.value] = { [key]: variant };
    }
  }
};

const setSizePrice = (sizeObj, priceObj, key) => {
  if (key in sizeObj) {
    const variant = sizeObj[key];
    const {
      sizeOption: { value: size },
    } = variant;

    if (size in priceObj) return;

    priceObj[size] = variant;
  }
};

const sortBySize = (a: IBuyBarProductVariant, b: IBuyBarProductVariant) => {
  if (a.sizeOption?.value < b.sizeOption?.value) {
    return -1;
  }
  if (a.sizeOption?.value > b.sizeOption?.value) {
    return 1;
  }
  return 0;
};

const getDisplayPrices = (variantObj) => {
  const sizeToPriceMap: {
    [key: number]: IBuyBarProductVariant;
  } = {};

  for (const size of Object.values(variantObj)) {
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.BEST_PRICE);
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.NEW_WITH_BOX_ISSUES);
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.NEW_WITH_DEFECTS);
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.USED);
  }

  return Object.values(sizeToPriceMap).sort(sortBySize);
};

export const getAvailableSizesPrioritizedByCondition = (
  productVariants: IBuyBarProductVariant[],
  getInstantShipLowestPrice = false,
  productTemplateSizeOptionsMap: Map<number, ISize> = null,
) => {
  // #TODO: Refactor: Combine all productVariants forLoop into one.
  const bestPriceVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_NO_DEFECTS],
    [BOX_CONDITIONS.GOOD_CONDITION],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const instantShipVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_NO_DEFECTS],
    [],
    true,
    productTemplateSizeOptionsMap,
  );

  const newWithBoxIssuesVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_NO_DEFECTS],
    [BOX_CONDITIONS.NO_ORIGINAL_BOX, BOX_CONDITIONS.BADLY_DAMAGED, BOX_CONDITIONS.MISSING_LID],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const newWithDefectsVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_WITH_DEFECTS],
    [],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const usedVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.USED, SHOE_CONDITIONS.GOAT_CLEAN],
    [],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const mapSizeToVariants: IMapSizeToVariants = {};

  createSizeToVariantObject(mapSizeToVariants, bestPriceVariant, VARIANT_KEYS.BEST_PRICE);
  createSizeToVariantObject(mapSizeToVariants, instantShipVariant, VARIANT_KEYS.INSTANT_SHIP);
  createSizeToVariantObject(
    mapSizeToVariants,
    newWithBoxIssuesVariant,
    VARIANT_KEYS.NEW_WITH_BOX_ISSUES,
  );
  createSizeToVariantObject(
    mapSizeToVariants,
    newWithDefectsVariant,
    VARIANT_KEYS.NEW_WITH_DEFECTS,
  );
  createSizeToVariantObject(mapSizeToVariants, usedVariant, VARIANT_KEYS.USED);

  return { availableSizes: getDisplayPrices(mapSizeToVariants), mapSizeToVariants, usedVariant };
};
